import React, { useEffect, useState } from 'react';
import ReactFlow, { Background, Controls, useNodesState, useEdgesState } from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { getBackendUrl } from '../../utils/server/apiHelper';
import { useAppTranslation } from '../../hooks/useAppTranslation';

const getNodeStyle = (isHighlight = false) => ({
    background: isHighlight
        ? 'linear-gradient(180deg, #fef9c3 0%, #fde68a 100%)'
        : 'var(--component-bg)',
    color: isHighlight ? '#1f2937' : 'var(--text-color)',
    border: isHighlight ? '2px solid #eab308' : '1px solid var(--border-color)',
    borderRadius: '16px',
    padding: '12px',
    width: 220,
    fontSize: '12px',
    fontWeight: isHighlight ? 700 : 500,
    boxShadow: '0 10px 20px rgba(15, 23, 42, 0.06)',
});

const mapCatalogToFlow = (catalogNodes) => {
    if (!catalogNodes) return { nodes: [], edges: [] };

    const nodes = [];
    const edges = [];

    catalogNodes.forEach((node, index) => {
        const metadata = node.metadata || {};
        const steps = metadata.followUpSteps?.steps || [];

        nodes.push({
            id: node.node_id,
            data: { label: node.content },
            position: { x: 250, y: index * 150 },
            style: getNodeStyle(false),
        });

        steps.forEach((step, sIndex) => {
            if (step.nextNodeId) {
                edges.push({
                    id: `e-${node.node_id}-${step.nextNodeId}-${sIndex}`,
                    source: node.node_id,
                    target: step.nextNodeId,
                    label: step.input ? String(step.input).substring(0, 15) : '',
                    animated: true,
                    style: { stroke: '#64748b', strokeWidth: 1.4 },
                    labelStyle: { fill: '#475569', fontSize: 11, fontWeight: 600 },
                });
            }
        });
    });

    return { nodes, edges };
};

export function GraphVisualizer({ highlightNodeId }) {
    const { t } = useAppTranslation();
    const { token } = useAuth();
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchGraph = async () => {
            if (!token) return;
            try {
                const BACKEND_URL = getBackendUrl();
                const response = await axios.get(`${BACKEND_URL}/catalog/graph`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const { nodes: flowNodes, edges: flowEdges } = mapCatalogToFlow(response.data.nodes);
                setNodes(flowNodes);
                setEdges(flowEdges);
            } catch (err) {
                console.error('Failed to load graph', err);
            } finally {
                setLoading(false);
            }
        };

        fetchGraph();
    }, [token, setNodes, setEdges]);

    useEffect(() => {
        setNodes((nds) =>
            nds.map((node) => {
                const isHighlight = highlightNodeId ? node.id === highlightNodeId : false;
                return {
                    ...node,
                    style: getNodeStyle(isHighlight),
                };
            })
        );
    }, [highlightNodeId, setNodes]);

    return (
        <Paper
            elevation={0}
            sx={{
                height: '100%',
                width: '100%',
                borderRadius: 6,
                overflow: 'hidden',
                backgroundColor: 'var(--component-bg)',
                backdropFilter: 'blur(20px)',
                border: '1px solid var(--border-color)',
                boxShadow: '0 30px 60px rgba(15, 23, 42, 0.18)',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{
                    p: 2,
                    borderBottom: '1px solid var(--border-color)',
                    backgroundColor: 'var(--component-bg)',
                }}
            >
                <Typography variant='h6' sx={{ fontWeight: 700, color: 'var(--text-color)', fontSize: '1rem' }}>
                    {t.processVisualizerTitle}
                </Typography>
                <Typography variant='caption' sx={{ color: 'var(--text-color-light)' }}>
                    {t.processVisualizerSubtitle}
                </Typography>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    position: 'relative',
                    backgroundColor: 'var(--component-bg-light)',
                    '& .react-flow__controls': {
                        boxShadow: 'none',
                        border: '1px solid var(--border-color)',
                        borderRadius: '12px',
                        overflow: 'hidden',
                    },
                    '& .react-flow__controls-button': {
                        backgroundColor: 'var(--component-bg)',
                        borderBottom: '1px solid var(--border-color)',
                        color: 'var(--text-color)',
                    },
                    '& .react-flow__controls-button:hover': {
                        backgroundColor: 'var(--component-bg-light)',
                    },
                    '& .react-flow__attribution': {
                        background: 'transparent',
                        color: 'var(--text-color-light)',
                    },
                    '& .react-flow__edge-textbg': {
                        fill: 'var(--component-bg)',
                    },
                }}
            >
                {loading ? (
                    <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
                        <CircularProgress />
                    </Box>
                ) : nodes.length === 0 ? (
                    <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
                        <Typography color='text.secondary'>{t.noProcessData}</Typography>
                    </Box>
                ) : (
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        fitView
                    >
                        <Background color='#94a3b8' gap={18} />
                        <Controls />
                    </ReactFlow>
                )}
            </Box>
        </Paper>
    );
}
